// The quiet reveal: small tiles stacked at the bottom right (above the bottom
// navigation on a phone). They stay until the member taps one (it opens in the
// collection and is collected) or clears them with "Collect all". More than three
// fold into "+N more". Copies of one badge share a tile with their count. Each
// tile says its tier (the rarity) and how many hold it; a first on the site gets
// a small "first" chip.
import React, { useState } from "react";
import { createPortal } from "react-dom";
import BadgeArt from "../art/BadgeArt";
import { haptic } from "../../../utils/haptics";
import { TierName } from "../TierName";
import { FirstChip } from "./FirstEver";
import { formatPoints, repeatLabel, revealLine, type TileGroup } from "./revealQueue";

export const TILE_STACK_VISIBLE = 3;

export interface BadgeTileStackProps {
  /** Worst first. */
  tiles: TileGroup[];
  /** Hidden while other floating chrome (a menu, the chat, a sheet) is open. */
  stashed?: boolean;
  /** Sit above the siteline dock chip (the desktop home page). */
  aboveDock?: boolean;
  onOpenTile: (group: TileGroup) => void;
  onCollectAll: (copyIds: string[]) => void;
}

const BadgeTileStack: React.FC<BadgeTileStackProps> = ({ tiles, stashed = false, aboveDock = false, onOpenTile, onCollectAll }) => {
  const [expanded, setExpanded] = useState(false);

  if (!tiles.length) return null;

  const hiddenCount = expanded ? 0 : Math.max(0, tiles.length - TILE_STACK_VISIBLE);
  const visible = expanded ? tiles : tiles.slice(0, TILE_STACK_VISIBLE);
  const allIds = tiles.flatMap((t) => t.copyIds);

  const classes = [
    "bdg-tiles",
    aboveDock ? "bdg-tiles--above-dock" : "",
    expanded ? "bdg-tiles--expanded" : "",
    stashed ? "is-stashed" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const node = (
    <section className={classes} aria-label="New badges" data-testid="badge-tile-stack" aria-hidden={stashed || undefined}>
      <ul className="bdg-tiles__list">
        {visible.map((group, i) => {
          const copy = group.latest;
          const repeat = repeatLabel(group.count);
          // the chip says first on the site; the line keeps to the tier and the holders
          const line = revealLine({ ...copy, firstEver: false });
          return (
            <li key={group.code} className="bdg-tiles__item" style={{ animationDelay: `${i * 70}ms` }}>
              <button
                type="button"
                className="bdg-tile"
                onClick={() => {
                  haptic("light");
                  onOpenTile(group);
                }}
                tabIndex={stashed ? -1 : undefined}
              >
                <BadgeArt
                  category={copy.category}
                  tier={copy.tier}
                  number={copy.number}
                  code={copy.code}
                  name={copy.name}
                  size={32}
                  variant="mini"
                  animate="reveal"
                />
                <span className="bdg-tile__text">
                  <span className="bdg-tile__name">{copy.name}</span>
                  <span className="bdg-tile__meta">
                    <TierName tier={copy.tier} />
                    <span className="bdg-tierline__sr">.</span>
                    {line.fact && <span className="bdg-tile__fact"> {line.fact}</span>}
                  </span>
                </span>
                <span className="bdg-tile__side">
                  {group.value !== null && <span className="bdg-tile__points">+{formatPoints(group.value)}</span>}
                  {repeat && <span className="bdg-tile__count">{repeat}</span>}
                  {group.firstEver && <FirstChip />}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      <div className="bdg-tiles__foot">
        {hiddenCount > 0 ? (
          <button type="button" className="bdg-tiles__more" onClick={() => setExpanded(true)} tabIndex={stashed ? -1 : undefined}>
            +{hiddenCount} more
          </button>
        ) : expanded && tiles.length > TILE_STACK_VISIBLE ? (
          <button type="button" className="bdg-tiles__more" onClick={() => setExpanded(false)} tabIndex={stashed ? -1 : undefined}>
            Show less
          </button>
        ) : (
          <span className="bdg-tiles__label">{tiles.length === 1 ? "New badge" : "New badges"}</span>
        )}
        <button
          type="button"
          className="bdg-tiles__all"
          onClick={() => {
            haptic("light");
            onCollectAll(allIds);
          }}
          tabIndex={stashed ? -1 : undefined}
        >
          Collect all
        </button>
      </div>
    </section>
  );

  return typeof document !== "undefined" ? createPortal(node, document.body) : node;
};

export default BadgeTileStack;
